/**
 * BattleCode V3 — Combat Referee (1v1 + multi-tank 战斗裁判)
 *
 * Phase 3: 从 engine-scheduler.ts 迁移伤害结算 + 胜负判定逻辑。
 * V3: 扩展支持多坦克 checkCondition + resolveDamage(target_index)
 */

import type { BattleState, BotAction, Position } from "../types";
import type { RefereeConfig, RulesConfig, ViolationType, WinCondition } from "../types";
import type { ConditionResult, Referee, ValidationResult, ViolationContext } from "./referee.interface";
import { TANK_STATS, chebyshevDistance } from "../tank-stats";
import type { TickContext } from "../modes/game-mode.interface";

/** V3: 获取队伍存活坦克数 */
function countAliveTanks(state: BattleState, team: "team_a" | "team_b"): number {
  if (state.team_a && state.team_b) {
    const teamState = team === "team_a" ? state.team_a : state.team_b;
    return teamState.tanks.filter((t) => t.is_alive).length;
  }
  // 1v1 向后兼容
  if (team === "team_a") return state.self.is_alive ? 1 : 0;
  return state.enemy.is_alive ? 1 : 0;
}

export class CombatReferee implements Referee {
  private refereeConfig: RefereeConfig;
  private rulesConfig: RulesConfig | null;
  private ctx: TickContext;
  /** V3: 每队各坦克 cooldown 独立维护 */
  private teamACooldowns: number[] = [0];
  private teamBCooldowns: number[] = [0];
  /** V1/V2: AI cooldown 向后兼容 */
  private enemyCooldownLeft: number = 0;
  /** V3: 多坦克模式标记 */
  private isMultiTank: boolean = false;

  constructor(config: RefereeConfig, ctx: TickContext, rules: RulesConfig | null = null) {
    this.refereeConfig = config;
    this.ctx = ctx;
    this.rulesConfig = rules;
    // V3: 检查是否为多坦克模式
    if (rules?.constraints?.max_units_per_team && rules.constraints.max_units_per_team > 1) {
      this.isMultiTank = true;
      const n = rules.constraints.max_units_per_team;
      this.teamACooldowns = new Array(n).fill(0);
      this.teamBCooldowns = new Array(n).fill(0);
    }
  }

  getConfig(): RefereeConfig {
    return this.refereeConfig;
  }

  /** V1/V2 backward compat */
  setEnemyCooldownLeft(value: number): void {
    this.enemyCooldownLeft = value;
  }

  getEnemyCooldownLeft(): number {
    return this.enemyCooldownLeft;
  }

  /** V3: 设置某坦克 cooldown */
  setTankCooldown(team: "team_a" | "team_b", tankIndex: number, value: number): void {
    if (team === "team_a") this.teamACooldowns[tankIndex] = value;
    else this.teamBCooldowns[tankIndex] = value;
  }

  /** V3: 获取某坦克 cooldown */
  getTankCooldown(team: "team_a" | "team_b", tankIndex: number): number {
    return team === "team_a" ? this.teamACooldowns[tankIndex] : this.teamBCooldowns[tankIndex];
  }

  /** V3: 维护所有存活坦克的 cooldown 自然冷却 */
  tickCooldowns(state: BattleState): void {
    if (!this.isMultiTank || !state.team_a || !state.team_b) {
      // 1v1 path
      if (state.self.cooldown_left > 0) state.self.cooldown_left -= 1;
      if (this.enemyCooldownLeft > 0) this.enemyCooldownLeft -= 1;
      return;
    }

    for (let i = 0; i < state.team_a.tanks.length; i++) {
      if (state.team_a.tanks[i].is_alive && this.teamACooldowns[i] > 0) this.teamACooldowns[i]--;
      state.team_a.tanks[i].cooldown_left = this.teamACooldowns[i];
    }
    for (let i = 0; i < state.team_b.tanks.length; i++) {
      if (state.team_b.tanks[i].is_alive && this.teamBCooldowns[i] > 0) this.teamBCooldowns[i]--;
      state.team_b.tanks[i].cooldown_left = this.teamBCooldowns[i];
    }
  }

  // ===== validateAction =====
  validateAction(action: BotAction, _state: BattleState, team: "team_a" | "team_b"): ValidationResult {
    const validTypes = ["move", "shoot", "scan", "idle"];
    if (!validTypes.includes(action.type)) {
      return { ok: false, code: "ACTION_BLOCKED", message: `unknown action type: ${action.type}` };
    }

    const blocked = this.rulesConfig?.api_surface?.blocked_apis;
    if (blocked && blocked.includes(action.type)) {
      this.onViolation("blocked_api", {
        tickNum: this.ctx.tickNum,
        team,
        detail: `action '${action.type}' is blocked in this level`,
      });
      return {
        ok: false,
        code: "ACTION_BLOCKED",
        message: `API '${action.type}' is blocked in this level (rules.json blocked_apis)`,
      };
    }

    return { ok: true };
  }

  // ===== checkCondition（V3: 支持多坦克）=====
  checkCondition(state: BattleState, tickNum: number, maxTicks: number): ConditionResult {
    if (this.rulesConfig) {
      for (const wc of this.rulesConfig.win_conditions) {
        const result = this.checkSingleCondition(wc, state, tickNum, maxTicks);
        if (result) return result;
      }
      return null;
    }

    // V3: 多坦克判定
    if (this.isMultiTank && state.team_a && state.team_b) {
      const aAlive = countAliveTanks(state, "team_a");
      const bAlive = countAliveTanks(state, "team_b");
      if (aAlive === 0) return { type: "win", team: "team_b", reason: "Team A eliminated" };
      if (bAlive === 0) return { type: "win", team: "team_a", reason: "Team B eliminated" };
      if (tickNum > maxTicks) return { type: "draw", reason: `tick limit reached (${maxTicks})` };
      return null;
    }

    // 1v1 向后兼容
    if (state.self.health <= 0) return { type: "win", team: "team_b", reason: "HP <= 0" };
    if (state.enemy.health <= 0) return { type: "win", team: "team_a", reason: "HP <= 0" };
    if (tickNum > maxTicks) return { type: "draw", reason: `tick limit reached (${maxTicks})` };
    return null;
  }

  private checkSingleCondition(wc: WinCondition, state: BattleState, tickNum: number, maxTicks: number): ConditionResult {
    switch (wc.type) {
      case "eliminate": {
        if (this.isMultiTank && state.team_a && state.team_b) {
          if (countAliveTanks(state, "team_a") === 0) return { type: "win", team: "team_b", reason: "Team A eliminated" };
          if (countAliveTanks(state, "team_b") === 0) return { type: "win", team: "team_a", reason: "Team B eliminated" };
          return null;
        }
        if (state.self.health <= 0) return { type: "win", team: "team_b", reason: "HP <= 0" };
        if (state.enemy.health <= 0) return { type: "win", team: "team_a", reason: "HP <= 0" };
        return null;
      }
      case "timeout": {
        const limit = wc.tick ?? maxTicks;
        if (tickNum > limit) return { type: "draw", reason: `tick limit reached (${limit})` };
        return null;
      }
      case "reach_zone": {
        const radius = wc.radius ?? 3;
        const dist = chebyshevDistance(state.self.position, state.enemy.position);
        if (dist <= radius) {
          return { type: "win", team: "team_a", reason: `reached enemy zone (dist=${dist}, radius=${radius})` };
        }
        return null;
      }
      default:
        return null;
    }
  }

  // ===== onViolation =====
  onViolation(type: ViolationType, context: ViolationContext): void {
    const handling = this.refereeConfig.violation_handling[type];
    if (!handling) return;
    if (handling.visibility === "silent") return;

    const channel = handling.feedback_channel;
    const msg = `[REFEREE] ${context.team} ${type}${context.detail ? ": " + context.detail : ""}`;

    if (channel === "console" || channel === "both") console.warn(msg);
    if (channel === "kill_feed" || channel === "both") {
      this.ctx.emit({
        type: "key_event",
        eventType: "COLLISION",
        tickNum: context.tickNum,
        actor: context.team,
        description: msg,
      });
    }
  }

  // ===== resolveDamage（V3: 支持 target_index + 多坦克）=====
  resolveDamage(
    shooterTeam: "team_a" | "team_b",
    action: BotAction,
    state: BattleState,
  ): number {
    if (action.type !== "shoot") return 0;

    const isShooterA = shooterTeam === "team_a";
    const accuracy = isShooterA ? TANK_STATS.player_accuracy : TANK_STATS.ai_accuracy;
    const targetIndex = action.target_index ?? 0;

    // V3: 多坦克模式 — 从 teams 取数据
    if (this.isMultiTank && state.team_a && state.team_b) {
      return this.resolveDamageMulti(shooterTeam, targetIndex, accuracy, state);
    }

    // 1v1 向后兼容
    let shooterPos: Position;
    let shooterCooldownLeft: number;
    let shooterAlive: boolean;

    if (isShooterA) {
      shooterPos = state.self.position;
      shooterCooldownLeft = state.self.cooldown_left;
      shooterAlive = state.self.is_alive;
    } else {
      shooterPos = state.enemy.position;
      shooterCooldownLeft = this.enemyCooldownLeft;
      shooterAlive = state.enemy.is_alive;
    }

    if (!shooterAlive) return 0;
    const targetHealth = isShooterA ? state.enemy.health : state.self.health;
    if (targetHealth <= 0) return 0;
    if (shooterCooldownLeft > 0) return 0;

    const targetPos = isShooterA ? state.enemy.position : state.self.position;
    const distance = chebyshevDistance(shooterPos, targetPos);
    if (distance > TANK_STATS.range) {
      // FR-027 修复: 超距射击也消耗 cooldown（开火动作发生了，与未命中一致）
      // 原 bug: 超距不消耗冷却 → bot 每tick射击 → move 被覆盖 → 永远不靠近 → 僵局
      if (isShooterA) state.self.cooldown_left = state.self.attack_cooldown_ticks;
      else this.enemyCooldownLeft = TANK_STATS.attack_cooldown_ticks;
      this.ctx.emit({
        type: "key_event",
        eventType: "SHOOT_FAILED",
        tickNum: this.ctx.tickNum,
        actor: isShooterA ? "team_a" : "team_b",
        description: `${isShooterA ? "玩家" : "AI"} shoot超距(dist=${distance},range=${TANK_STATS.range})`,
      });
      return 0;
    }

    if (Math.random() >= accuracy) {
      if (isShooterA) state.self.cooldown_left = state.self.attack_cooldown_ticks;
      else this.enemyCooldownLeft = TANK_STATS.attack_cooldown_ticks;
      return 0;
    }

    if (isShooterA) state.self.cooldown_left = state.self.attack_cooldown_ticks;
    else this.enemyCooldownLeft = TANK_STATS.attack_cooldown_ticks;
    return TANK_STATS.damage;
  }

  /** V3: 多坦克伤害结算 */
  private resolveDamageMulti(
    shooterTeam: "team_a" | "team_b",
    shooterIndex: number,
    accuracy: number,
    state: BattleState,
  ): number {
    const shooterTeamState = shooterTeam === "team_a" ? state.team_a! : state.team_b!;
    const targetTeamState = shooterTeam === "team_a" ? state.team_b! : state.team_a!;

    const shooter = shooterTeamState.tanks[shooterIndex];
    if (!shooter || !shooter.is_alive) return 0;

    const cooldowns = shooterTeam === "team_a" ? this.teamACooldowns : this.teamBCooldowns;
    if (cooldowns[shooterIndex] > 0) return 0;

    // 找最近的存活敌人
    let bestTarget: { index: number; dist: number } | null = null;
    for (let i = 0; i < targetTeamState.tanks.length; i++) {
      const t = targetTeamState.tanks[i];
      if (!t.is_alive) continue;
      const d = chebyshevDistance(shooter.position, t.position);
      if (!bestTarget || d < bestTarget.dist) bestTarget = { index: i, dist: d };
    }
    if (!bestTarget) return 0;

    if (bestTarget.dist > TANK_STATS.range) {
      // FR-027 修复: 超距射击也消耗 cooldown（与 1v1 路径一致）
      cooldowns[shooterIndex] = TANK_STATS.attack_cooldown_ticks;
      return 0;
    }

    if (Math.random() >= accuracy) {
      cooldowns[shooterIndex] = TANK_STATS.attack_cooldown_ticks;
      return 0;
    }

    cooldowns[shooterIndex] = TANK_STATS.attack_cooldown_ticks;
    return TANK_STATS.damage;
  }
}
